import React, {useEffect, useState,useMemo} from 'react';
import { useTranslation } from 'react-i18next';
import {Col, Row,Button,Divider,Select,Radio,DatePicker, RadioChangeEvent, message, Modal} from 'antd';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import ParcelFilterStyle from "../../../../delivery/components/ParcelFilterStyle";
import PricingItemPanel from '../PriceItemPanel';
import PickupFeeViewTab from './PickupFeeViewTab'
import DeliveryFeeViewTab from './DeliveryFeeViewTab'
import RouteFeeViewTab from './RouteFeeViewTab'
import BizTypeFeeViewTab from './BizTypeFeeViewTab'
import OverWeightFeeViewTab from './OverWeightFeeViewTab'
import OverVolumeFeeViewTab from './OverVolumeFeeViewTab'
import FuelSurchargeRateViewTab from './FuelSurchargeRateViewTab'
import { ChannelSelectFilter } from '../../../../../components/settlement/ChannelSelectFilter';
import dayjs from 'dayjs';
import { LIST_SCHEME_SELECT_OPTIONS } from '../graphql/queries/listSchemeSelectOptions';
import {GET_PRICING_SCHEME_DETAIL, PricingSchemeDetailResponse} from '../graphql/queries/getPricingSchemeDetail';
import {LIST_HUB_SORT_CODE_SELECT_OPTIONS } from './graphql/queries/listAllHubSortCodeOptions';
import {
    GET_CLIENT_PRICING_SCHEME_APPLY_DETAIL,
    ClientPricingApplyDetailResponse
} from './graphql/queries/getClientPricingSchemeApplyDetail';
import {
    BIND_PRICING_SCHEME,
    UNBIND_PRICING_SCHEME,
    BindPricingSchemeInput,
    UnbindPricingSchemeInput,
    BindPricingSchemeResponse,
    UnbindPricingSchemeResponse
} from './graphql/mutations/pricingSchemeApplyMutation';
import type { SelectOptionType } from 'graphql/types/SelectOptionType';
import {HasResource} from "../../../../../components/HasResource";
import {ResourceCode} from "../../../../../constants/resourceCodes";
import { resolvePricingSchemeMessage, translateGraphQLError } from "../../../../../utils/graphqlUtils";
import { formatApplyTimeWithTimezone } from "../../../../../utils/timezoneFormatter";

const ApplySchemeComp: React.FC<{
    businessScenario: number | null;
    isBindView : boolean;
    onClose?: () => void;
    onSuccess?: () => void; // 成功后的回调，用于刷新列表
}> = ({businessScenario, isBindView, onClose, onSuccess}) => {
    const { t } = useTranslation();

    // 获取客户列表
    const [clientId, setClientId] = useState<number | null>(null);
    const [clientName, setClientName] = useState<string>('');
    const [schemeDesc, setSchemeDesc] = useState<string>('');
    const [schemeOptions, setSchemeOptions] = useState<SelectOptionType[]>([]);
    const [hubSortCodeOptions, setHubSortCodeOptions] = useState<SelectOptionType[]>([]);
    const [selectedSchemeId, setSelectedSchemeId] = useState<number | null>(null);
    const [feeItemList, setFeeItemList] = useState<Array<{ label: string; key: string; children: JSX.Element }>>([]);
    const [selectedHubs, setSelectedHubs] = useState<string[]>([]);
    const [unbindHubs, setUnbindHubs] = useState<string[]>([]);
    const [effectiveDate, setEffectiveDate] = useState<dayjs.Dayjs | null>(null);
    const [timeZone, setTimeZone] = useState<string>('Asia/Shanghai');


    // 获取方案列表的懒加载查询
    const [loadSchemeOptions, { data: schemeData, loading: schemeLoading }] = useLazyQuery<{allOptions : SelectOptionType[]}>(
        LIST_SCHEME_SELECT_OPTIONS
    );

    // 获取方案详情的懒加载查询
    const [loadSchemeDetail, { data: schemeDetailData, loading: schemeDetailLoading }] = useLazyQuery<PricingSchemeDetailResponse>(
        GET_PRICING_SCHEME_DETAIL,
        {
            fetchPolicy: 'network-only', // 每次都从网络获取最新数据，不使用缓存
        }
    );

    const [loadHubSortCodeOptions, { data: hubSortCodeData, loading: hubSortCodeLoading }] = useLazyQuery<{allOptions:SelectOptionType[]}>(
        LIST_HUB_SORT_CODE_SELECT_OPTIONS
    );

    // 获取客户定价方案应用详情（解绑时使用）
    const [loadApplyDetail, { data: applyDetailData, loading: applyDetailLoading }] = useLazyQuery<ClientPricingApplyDetailResponse>(
        GET_CLIENT_PRICING_SCHEME_APPLY_DETAIL,
        {
            fetchPolicy: 'network-only', // 每次都从网络获取最新数据，不使用缓存
        }
    );

    // Mutations
    const [bindPricingScheme, { loading: bindLoading }] = useMutation<BindPricingSchemeResponse>(BIND_PRICING_SCHEME);
    const [unbindPricingScheme, { loading: unbindLoading }] = useMutation<UnbindPricingSchemeResponse>(UNBIND_PRICING_SCHEME);

    const feeItemTabMap: Record<string, { label: string; componentType: string }> = {
        '1001001001': {'label':'配送费', 'componentType': 'delivery'},
        '1001004055': {'label':'取件费（TT）', 'componentType': 'bizType'},
        '1001004056': {'label':'分拣费（TT）', 'componentType': 'pickup'},
        '1001001057': {'label':'线路费', 'componentType': 'route'},
        '1001001006': {'label':'转运费', 'componentType': 'route'},
        '1001001004': {'label':'超重费', 'componentType': 'overWeight'},
        '1001001007': {'label':'超长费', 'componentType': 'overVolume'},
        '1001001059': {'label':'退件费（TT）', 'componentType': 'delivery'},
        // 1001004061：卸车费（TT）。注意 1001001060 已被 "TT预分拣费"
        // （SettlementConstants.BusinessCode.TT_PRESORT_FEE_ITEM_CODE）占用，是完全不同的费用项，
        // 卸车费（TT）实际落库用的是 1001004061。
        // 取件费/分拣费/卸车费三项code已从 1001001055/1001001056/1001001061 改为
        // 1001004055/1001004056/1001004061（前缀与所属计费场景1001004对齐）。
        '1001004061': {'label':'卸车费（TT）', 'componentType': 'bizType'},
        // 燃油附加费：金额 = 基础运费(1001001001) × 燃油费率(%)
        '1001001062': {'label':'燃油附加费', 'componentType': 'fuelSurchargeRate'},
    };


    // 处理客户选择变化
    const handleClientChange = (value: string | undefined) => {
        if (value) {
            const newClientId = parseInt(value, 10);
            // 只有当客户真正改变时才清空数据
            if (newClientId !== clientId) {
                setClientId(newClientId);
                // 清空下级联动数据
                setSelectedSchemeId(null);
                setSchemeOptions([]);
                setSchemeDesc('');
                setFeeItemList([]);
                setSelectedHubs([]);
                setUnbindHubs([]);
                setEffectiveDate(null);
                setTimeZone('Asia/Shanghai');
            }
        } else {
            setClientId(null);
            setClientName('');
            setSchemeOptions([]);
            setSelectedSchemeId(null);
            setSchemeDesc('');
            setFeeItemList([]);
            setSelectedHubs([]);
            setUnbindHubs([]);
            setEffectiveDate(null);
            setTimeZone('Asia/Shanghai');
        }
    };

    // 处理客户选择（获取客户名称）
    const handleClientSelect = (value: string, option: { label: string; value: string }) => {
        setClientName(option.label);
        setClientId(parseInt(value, 10));
    };

    // 处理方案选择变化
    const handleSchemeChange = (value: string) => {
       
        if (value) {
            const schemeId = parseInt(value, 10);
            setSelectedSchemeId(schemeId);
            // 清空下级联动数据
            setSelectedHubs([]);
            setUnbindHubs([]);
            setEffectiveDate(null);
            setTimeZone('Asia/Shanghai');
        } else {
            setSelectedSchemeId(null);
            setSchemeDesc('');
            setFeeItemList([]);
            setSelectedHubs([]);
            setUnbindHubs([]);
            setEffectiveDate(null);
            setTimeZone('Asia/Shanghai');
        }
    };

    // 处理选择维度变化（绑定）
    const handleSelectedHubsChange = (value: string[]) => {
        setSelectedHubs(value);
    };

    // 处理解绑维度变化
    const handleUnbindHubsChange = (value: string[]) => {
        setUnbindHubs(value);
    };

    // 重置所有状态（关闭或返回时清空数据）
    const resetAllStates = () => {
        setClientId(null);
        setClientName('');
        setSchemeDesc('');
        setSchemeOptions([]);
        setSelectedSchemeId(null);
        setFeeItemList([]);
        setSelectedHubs([]);
        setUnbindHubs([]);
        setEffectiveDate(null);
        setTimeZone('Asia/Shanghai');
    };

    // 处理返回/关闭
    const handleClose = () => {
        resetAllStates();
        onClose?.();
    };

    // 处理日期变化
    const handleDateChange = (date: dayjs.Dayjs | null) => {
        setEffectiveDate(date);
    };

    // 处理时区变化
    const handleTimeZoneChange = (value: string) => {
        setTimeZone(value);
    };

    // 验证表单
    const validateForm = (): boolean => {
        if (!clientId) {
            message.error('请选择客户');
            return false;
        }
        if (!selectedSchemeId) {
            message.error('请选择方案');
            return false;
        }
        if (selectedHubs.length === 0) {
            message.error('请选择维度');
            return false;
        }
        if (!timeZone) {
            message.error('请选择时区');
            return false;
        }
        if (checkEffectType === 1) {
            // 立即生效，不需要额外验证
            return true;
        } else if (checkEffectType === 2) {
            // 未来生效，需要验证日期和时区
            if (!effectiveDate) {
                message.error('请选择生效日期');
                return false;
            }
            
            return true;
        }
        return true;
    };

    // 处理保存
    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        // 构建提交数据
        const input: BindPricingSchemeInput = {
            clientId: clientId!,
            pricingSchemeId: selectedSchemeId!,
            bindValue: selectedHubs,
            effectiveNow: checkEffectType === 1, // true: 立即生效, false: 未来生效
            effectiveDate: checkEffectType === 2 ? effectiveDate?.format('YYYY-MM-DD') : null,
            timezone: timeZone
        };

        try {
            const { data } = await bindPricingScheme({
                variables: { input }
            });

            if (data?.bindPricingSchema?.success) {
                message.success(t('settlement.priceCenter.messages.bindSchemeSuccess'));
                 setTimeout(() => {
                    // 触发刷新列表
                    onSuccess?.();
                 },1000);

                // 延迟关闭抽屉，让用户看到成功提示
                setTimeout(() => {
                    resetAllStates();
                    onClose?.();
                }, 700);
            }else{
                message.error(resolvePricingSchemeMessage(data?.bindPricingSchema, t('settlement.priceCenter.messages.bindSchemeFailed')));
            }
        } catch (error: any) {
            message.error(translateGraphQLError(error, 'errors.INTERNAL_ERROR'));
        }
    };

    // 处理解绑保存（带二次确认）
    const handleUnbindSave = () => {
        if (!clientId) {
            message.error('请选择客户');
            return;
        }
        if (!selectedSchemeId) {
            message.error('请选择方案');
            return;
        }
        if (unbindHubs.length === 0) {
            message.error('请选择要解绑的维度');
            return;
        }

        // 二次确认 - 强提示
        Modal.confirm({
            title: '确认解绑方案',
            icon: <span style={{ color: '#ff4d4f', fontSize: '22px' }}>⚠️</span>,
            content: (
                <div>
                    <p style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px', color: '#ff4d4f' }}>
                        警告：此操作不可撤销！
                    </p>
                    <p style={{ marginBottom: '8px' }}>您即将解绑以下信息：</p>
                    <div style={{ 
                        padding: '12px', 
                        background: '#fff7e6', 
                        border: '1px solid #ffd591',
                        borderRadius: '4px',
                        marginTop: '12px'
                    }}>
                        <p><strong>客户：</strong>{clientName} (ID: {clientId})</p>
                        <p><strong>方案ID：</strong>{selectedSchemeId}</p>
                        <p><strong>解绑维度：</strong>{unbindHubs.join(', ')}</p>
                    </div>
                    <p style={{ marginTop: '16px', color: '#ff4d4f' }}>
                        解绑后，该方案将不再对这些维度生效。
                    </p>
                </div>
            ),
            okText: '确认解绑',
            okType: 'danger',
            cancelText: '取消',
            width: 520,
            onOk: async () => {
                // 构建提交数据
                const input: UnbindPricingSchemeInput = {
                    clientId: clientId,
                    pricingSchemeId: selectedSchemeId,
                    unBindValue: unbindHubs,
                };

                try {
                    const { data } = await unbindPricingScheme({
                        variables: { input }
                    });

                    if (data?.unbindPricingSchema?.success) {
                        message.success(t('settlement.priceCenter.messages.unbindSchemeSuccess'));
                        // 触发刷新列表
                        onSuccess?.();
                        // 延迟关闭抽屉，让用户看到成功提示
                        setTimeout(() => {
                            resetAllStates();
                            onClose?.();
                        }, 500);
                    } else {
                        message.error(resolvePricingSchemeMessage(data?.unbindPricingSchema, t('settlement.priceCenter.messages.unbindSchemeFailed')));
                    }
                } catch (error: any) {
                    message.error(translateGraphQLError(error, 'errors.INTERNAL_ERROR'));
                    throw error; // 让 Modal 保持打开状态
                }
            },
        });
    };

    // 当clientId变化时，加载对应的方案选项
    useEffect(() => {
        if (clientId !== null && businessScenario !== null) {
            loadSchemeOptions({
                variables: {
                    businessScenario: businessScenario,
                    channelId: clientId,
                    isBind: !isBindView
                }
            });
        } else {
            // 客户未选择或业务场景缺失时，清空方案选项
            setSchemeOptions([]);
        }
    }, [clientId, businessScenario, isBindView, loadSchemeOptions]);

    // 只在组件挂载时加载Hub选项（用于绑定操作）
    useEffect(() => {
        if (isBindView) {
            loadHubSortCodeOptions();
        }
    }, [isBindView, loadHubSortCodeOptions]);

    // 更新方案选项
    useEffect(() => {
        if (schemeData?.allOptions) {
            setSchemeOptions(schemeData.allOptions);
        }
         if (hubSortCodeData?.allOptions) {
            setHubSortCodeOptions(hubSortCodeData.allOptions);
        }
    }, [schemeData, hubSortCodeData, selectedSchemeId, clientId]);

    // 当选择方案时，加载方案详情或应用详情
    useEffect(() => {

        // 先清空之前的数据
        setSchemeDesc('');
        setFeeItemList([]);

        if (!selectedSchemeId) {
            return;
        }

        if (isBindView) {
            // 绑定视图：加载方案详情
            loadSchemeDetail({
                variables: {
                    pricingSchemeId: selectedSchemeId
                }
            });
        } else {
            // 解绑视图：加载应用详情
            if (clientId) {
                loadApplyDetail({
                    variables: {
                        clientId: clientId,
                        pricingSchemeId: selectedSchemeId
                    }
                });
            }
            // 同时也加载方案详情用于显示价格配置
            loadSchemeDetail({
                variables: {
                    pricingSchemeId: selectedSchemeId
                }
            });
        }

    }, [selectedSchemeId, clientId, isBindView, loadSchemeDetail, loadApplyDetail]);

    // 处理方案详情数据
    useEffect(() => {
        const detailResult = schemeDetailData?.getPricingSchemeDetail;
        if (detailResult?.messageKey || detailResult?.message) {
            message.error(resolvePricingSchemeMessage(detailResult, '加载定价方案详情失败'));
        }
        if (schemeDetailData?.getPricingSchemeDetail) {
            const detail = schemeDetailData.getPricingSchemeDetail;
            setSchemeDesc(detail?.pricingSchemeDescription || '');
            
            // 构建新的 feeItemList
            const newFeeItemList: Array<{ label: string; key: string; children: JSX.Element }> = [];
            detail.priceConfig.forEach((priceConfig) => {
                const feeItemCode = priceConfig.pricingFeeItemCode.toString();
                const feeItemTab = feeItemTabMap[feeItemCode];
                
                if (feeItemTab) {
                    const newPriceItems = priceConfig.allPriceConfig.map((config) => {
                        var obj = {
                            groupName: config.primaryFactorConfigDesc,
                            priceItems: JSON.parse(config.contentPriceConfig)
                        }
                        return obj;
                    });

                    // 根据组件类型创建相应的组件，直接传递 props
                    let component: JSX.Element;
                    if (feeItemTab.componentType === 'delivery') {
                        component = <DeliveryFeeViewTab renderData={newPriceItems} />;
                    } else if (feeItemTab.componentType === 'route') {
                        component = <RouteFeeViewTab renderData={newPriceItems} />;
                    } else if (feeItemTab.componentType === 'overWeight') {
                        component = <OverWeightFeeViewTab renderData={newPriceItems} />;
                    } else if (feeItemTab.componentType === 'overVolume') {
                        component = <OverVolumeFeeViewTab renderData={newPriceItems} />;
                    } else if (feeItemTab.componentType === 'bizType') {
                        component = <BizTypeFeeViewTab renderData={newPriceItems} />;
                    } else if (feeItemTab.componentType === 'fuelSurchargeRate') {
                        component = <FuelSurchargeRateViewTab renderData={newPriceItems} />;
                    } else {
                        component = <PickupFeeViewTab renderData={newPriceItems} />;
                    } 
                    
                    newFeeItemList.push({
                        label: feeItemTab.label,
                        key: feeItemCode,
                        children: component
                    });
                }
            });
            setFeeItemList(newFeeItemList);
        } else {
            // 当没有方案详情数据时，清空feeItemList
            setFeeItemList([]);
        }
    }, [schemeDetailData]);
   
    const timeZoneOptions = [
        { value: 'Asia/Shanghai', label: 'Asia/Shanghai' },
        { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
    ];

    useEffect(() => {
        const applyDetail = applyDetailData?.getClientPricingSchemeApplyDetail;
        if (applyDetail?.messageKey || applyDetail?.message) {
            message.error(resolvePricingSchemeMessage(applyDetail, '加载客户方案绑定详情失败'));
        }
    }, [applyDetailData]);

    // 从应用详情中获取的已绑定信息
    const bindHubs = useMemo(() => {
        if (applyDetailData?.getClientPricingSchemeApplyDetail) {
            return applyDetailData.getClientPricingSchemeApplyDetail.bindValueDesc ||
                   applyDetailData.getClientPricingSchemeApplyDetail.bindValues.join(', ');
        }
        return '';
    }, [applyDetailData]);

    const timezone = useMemo(() => {
        if (applyDetailData?.getClientPricingSchemeApplyDetail) {
            return applyDetailData.getClientPricingSchemeApplyDetail.timezone || '';
        }
        return '';
    }, [applyDetailData]);

    const bindEffectiveTime = useMemo(() => {
        const detail = applyDetailData?.getClientPricingSchemeApplyDetail;
        if (detail?.effectiveTime) {
            return formatApplyTimeWithTimezone(detail.effectiveTime, timezone);
        }
        return '';
    }, [applyDetailData, timezone]);

    // 解绑维度的选项（从已绑定的维度中获取）
    const unbindOptions = useMemo(() => {
        if (applyDetailData?.getClientPricingSchemeApplyDetail) {
            return applyDetailData.getClientPricingSchemeApplyDetail.bindValues.map(value => ({
                value: value,
                label: value
            }));
        }
        return []; // 没有应用详情时返回空数组
    }, [applyDetailData]);

    const [checkEffectType, setCheckEffectType] = useState(1);

    const onChange = (e: RadioChangeEvent) => {
        setCheckEffectType(e.target.value);
    };
    
    const dateFormat = 'YYYY/MM/DD';
    const plainOptions = [{
        value:1, 
        label:'立即生效',
    },{
        value:2,
        label:(<>
            <span style={{marginRight:'10px'}}>未来生效</span>
            {checkEffectType === 2 && (
            <span>
                <DatePicker 
                    value={effectiveDate} 
                    onChange={handleDateChange}
                    format={dateFormat}
                    minDate={dayjs().add(1, 'day').startOf('day')}
                    placeholder="选择生效日期"
                />
            </span>
            )}
        </>),
    }];

    const RadioStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    };

    return (
        <div>
            <Row justify="start"  gutter={[16, 8]}>
                <Col span={12} >
                    <ChannelSelectFilter
                        label="客户名称"
                        value={clientId?.toString() || ''}
                        onChange={handleClientChange}
                        onSelect={handleClientSelect}
                        spanClassName="w-[9em]"
                    />
                </Col>
                <Col span={12} >
                    <div style={{margin:5}}>
                        {clientId && clientName ? (
                            <p>客户名称: <span>【#{clientId}】&nbsp;&nbsp;</span>{clientName}</p>
                        ) : (
                            <p>请选择客户</p>
                        )}
                    </div>
                </Col>
            </Row>
            <Divider />
            <Row justify="start"  gutter={[16, 8]}>
                <Col span={12} >
                    <ParcelFilterStyle
                        label="选择方案"
                        options={schemeOptions}
                        spanClassName="w-[9em]"
                        value={selectedSchemeId?.toString() || ''}
                        onChange={handleSchemeChange}
                        disabled={schemeLoading || !clientId}
                        placeholder={!clientId ? "请先选择客户" : "请选择方案"}
                    />
                </Col>
                <Col span={12} >
                    <p style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{schemeDesc}</p>
                </Col>
            </Row>
             <Row justify="center"  gutter={[8, 16]}>
                <Col span={24} />
                <Col >
                    <PricingItemPanel 
                        priceItemTabs={feeItemList}
                    />
                </Col>
             </Row>
             <Divider />
          
            {/* 判断是绑定还是解绑 */}
            {isBindView ? (
             <div >
                <Row justify="center" align='middle'  gutter={[8, 16]}>
                    <Col span={3} >
                        <span className={`text-gray-500 mr-2 text-left break-words min-w-0`}>选择维度</span>
                    </Col>
                    <Col span={18} >
                        <Select
                            mode="multiple"
                            size='large'
                            style={{ width: '100%' }}
                            value={selectedHubs}
                            onChange={handleSelectedHubsChange}
                            // 选中后保留搜索词，便于连续选择同一前缀下的多个维度
                            autoClearSearchValue={false}
                            options={hubSortCodeOptions}
                            disabled={hubSortCodeLoading || !selectedSchemeId}
                            placeholder={!selectedSchemeId ? "请先选择方案" : "请选择Hub维度"}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row justify="center" align='middle'  gutter={[8, 16]}>
                    <Col span={5} >
                        <span className={`text-gray-500 mr-2 text-left break-words min-w-0`}>生效时间</span>
                    </Col>
                    <Col span={10} >
                        <Radio.Group 
                            style={RadioStyle} 
                            options={plainOptions} 
                            value={checkEffectType}
                            onChange={onChange}
                            disabled={!selectedSchemeId}
                        />
                    </Col>
                    <Col span={6} >
                    <span style={{marginLeft:'10px'}}>时区</span>
                        <Select 
                            style={{ minWidth: 180, marginLeft:'10px' }} 
                            options={timeZoneOptions}
                            value={timeZone}
                            onChange={handleTimeZoneChange}
                        />
                    </Col>
                    
                </Row>
                <Row justify="end" style={{margin:25}}>
                    <Col>
                        <Button type="default" size="large" style={{margin:15}} disabled={bindLoading} onClick={handleClose}>返回</Button>
                        <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_PRICE_APPLY_OPERATE]}>
                            <Button type="primary" size="large" style={{margin:15}} onClick={handleSave} loading={bindLoading}>保存</Button>
                        </HasResource>
                    </Col>
                </Row>
             </div>
            ): (
             <div>   
                <Row justify="center" align='middle'  gutter={[8, 16]}>
                    <Col span={3} >
                        <span className={`text-gray-500 mr-2 text-left break-words min-w-0`}>已绑定维度</span>
                    </Col>
                    <Col span={18} >
                        <span>{bindHubs}</span>
                    </Col>
                </Row>
                <Divider />
                <Row justify="center" align='middle'  gutter={[8, 16]}>
                    <Col span={3} >
                        <span className={`text-gray-500 mr-2 text-left break-words min-w-0`}>生效时间</span>
                    </Col>
                    <Col span={18} >
                        <span>{bindEffectiveTime || '暂无数据'}</span>
                        &nbsp;&nbsp;&nbsp;&nbsp;
                        <span>{timezone || ''}</span>
                    </Col>
                </Row>
                 <Divider />
                 <Row justify="center" align='middle'  gutter={[8, 16]}>
                    <Col span={3} >
                        <span className={`text-gray-500 mr-2 text-left break-words min-w-0`}>解绑维度</span>
                    </Col>
                    <Col span={18} >
                        <Select
                            mode="multiple"
                            size='large'
                            style={{ width: '100%' }}
                            value={unbindHubs}
                            onChange={handleUnbindHubsChange}
                            // 选中后保留搜索词，便于连续选择同一前缀下的多个维度
                            autoClearSearchValue={false}
                            options={unbindOptions}
                            disabled={applyDetailLoading || !selectedSchemeId || unbindOptions.length === 0}
                            placeholder={!selectedSchemeId ? "请先选择方案" : unbindOptions.length === 0 ? "暂无可解绑的维度" : "请选择要解绑的Hub维度"}
                        />
                    </Col>
                </Row>
                 <Row justify="end" style={{margin:25}}>
                    <Col>
                        <Button type="default" size="large" style={{margin:15}} disabled={unbindLoading} onClick={handleClose}>返回</Button>
                        <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_PRICE_APPLY_OPERATE]}>
                            <Button type="primary" size="large" style={{margin:15}} onClick={handleUnbindSave} loading={unbindLoading}>确定解绑</Button>
                        </HasResource>
                    </Col>
                </Row>
             </div>
             )}
        </div>
    );
}

export default ApplySchemeComp;